/**
 * Purpose: Defines <vv-header-image-distributor> - a vanilla custom element that
 *          CLONES self-rendered menu-image cards out of the hidden header pool
 *          (#vv-header-image-pool, populated by `header-menu-image` blocks via
 *          `content_for 'blocks'`) into the matching desktop mega-menu panel
 *          (#submenu-{N}) and mobile drawer accordion. It re-runs on every
 *          header morph/re-render (Section Rendering API) so the injected cards
 *          survive the morph that would otherwise revert a one-shot move.
 *
 * Public API: the <vv-header-image-distributor> custom element. DOM contract
 *   (set by sections/header.liquid + blocks/header-menu-image.liquid):
 *     #vv-header-image-pool                         the hidden pool (required)
 *     [data-vv-menu-image-card="desktop"]           a desktop card wrapper
 *     [data-vv-menu-image-card="mobile"]            a mobile card wrapper
 *       data-menu-image-position="N"                1-based target nav index
 *       data-menu-image-wide="true"                 desktop: spans 2 columns
 *       data-menu-image-aspect="<css>"              desktop: aspect-ratio value
 *   Injected clones are marked [data-vv-menu-image-clone] and removed/re-created on
 *   each run (idempotent, morph-safe). Originals are never detached.
 *
 *   REPLACE behavior: when a menu-image card is injected into a desktop list /
 *   mobile drawer list that also holds server-rendered legacy metaobject image
 *   cards, those legacy (non-clone) cards are HIDDEN (JS-only, reversible via
 *   [data-vv-menu-image-legacy-hidden] + inline display:none) so the menu-image
 *   card is the panel's image-card content. Layout counts (renderedCount /
 *   reservedUnits / single-vs-multiple) reflect the injected menu-image cards
 *   ONLY, never the hidden legacy cards. JS off => the hide is never applied =>
 *   legacy cards stay visible. Nav-link columns (non-card content) are never
 *   touched.
 *
 * Upstream deps: DOM APIs only (no imports). Reads #submenu-{N} panels rendered
 *   by blocks/_header-menu.liquid and the mobile drawer rendered by
 *   snippets/header-drawer.liquid; relies on the morph system in
 *   assets/morph.js / assets/section-renderer.js as the re-render trigger it
 *   defends against.
 *
 * Downstream consumers: none (leaf component). The cards it injects reuse the
 *   .mega-menu__* and .menu-drawer__metaobject-* class families so the existing
 *   header CSS styles them with no new rules.
 *
 * Failure modes: graceful degradation - JS off or any thrown error leaves the
 *   cards hidden in the pool and the nav unaffected (every public entry point is
 *   wrapped in try/catch). A position with no matching #submenu-{N} or drawer
 *   <li> is a no-op logged via console.debug (no panel synthesis). All listeners
 *   and the MutationObserver are bound to one AbortController / disconnected in
 *   disconnectedCallback, so re-connection is safe and leak-free. The observer
 *   ignores its own injections (re-entrancy guard) to avoid self-trigger loops.
 *
 * Performance: O(cards + panels) synchronous DOM work per distribute() run;
 *   runs are rAF-debounced and coalesced. No timers, no network, no I/O. Cloned
 *   images are lazy-loaded; the pool stays hidden so off-screen images are not
 *   fetched until a clone is displayed.
 */

const CLONE_ATTR = 'data-vv-menu-image-clone';
const MOBILE_DEFAULT_ASPECT = '16/9';

/**
 * Marker for legacy (server-rendered) metaobject image cards we hide when a
 * menu-image card replaces them. JS-only + reversible: the inline `display:none`
 * and this attribute are applied/cleared by distribute() alone, so JS-off leaves
 * the legacy cards visible and the server HTML never carries the marker.
 */
const LEGACY_HIDDEN_ATTR = 'data-vv-menu-image-legacy-hidden';

class VVHeaderImageDistributor extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** Re-entrancy guard so our own injections don't re-trigger the observer. */
  #distributing = false;

  /** @type {number | null} rAF id for debounced re-runs. */
  #rafId = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Theme-editor section reload (belt-and-suspenders alongside the observer).
    document.addEventListener('shopify:section:load', this.#scheduleDistribute, { signal });
    document.addEventListener('shopify:section:reorder', this.#scheduleDistribute, { signal });

    // Re-run on every header morph/re-render. The Section Rendering API morph
    // reconciles the menu subtree and removes our clones (no server counterpart),
    // so we watch the header for childList changes and re-inject. The
    // re-entrancy guard below keeps our own injections from looping.
    this.#observer = new MutationObserver(this.#onMutations);
    const header = this.closest('header-component') ?? document.querySelector('header-component') ?? document.body;
    this.#observer.observe(header, { childList: true, subtree: true });

    // Initial pass after layout so #submenu-{N} panels exist.
    requestAnimationFrame(() => this.distribute());
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * MutationObserver callback. Any childList change outside the pool (a header
   * morph / hydration / section re-render) schedules a debounced re-run. Records
   * produced by our OWN injection are never seen here: distribute() drains them
   * via observer.takeRecords() under the #distributing guard before they are
   * delivered, so this callback only ever sees foreign mutations - including the
   * morph stripping our clones, which is precisely when we must re-inject.
   * @param {MutationRecord[]} records
   */
  #onMutations = (records) => {
    if (this.#distributing) return;

    for (const record of records) {
      // Ignore mutations confined to the pool itself (server re-rendering the
      // pool's own cards on morph); those are our source of truth, not a target.
      if (record.target instanceof Element && record.target.closest('#vv-header-image-pool')) {
        continue;
      }
      this.#scheduleDistribute();
      return;
    }
  };

  /** Debounce distribute() to one run per frame. */
  #scheduleDistribute = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.distribute();
    });
  };

  /**
   * Remove any previously-injected clones, then clone every pooled card into its
   * matching desktop panel and mobile drawer item. Fully idempotent.
   *
   * The #distributing guard plus observer.takeRecords() makes this self-trigger
   * safe: while we mutate the DOM the guard is set, and before releasing it we
   * discard the MutationRecords our own writes produced so they are never
   * delivered to #onMutations (which would otherwise loop).
   */
  distribute() {
    if (this.#distributing) return;
    this.#distributing = true;
    try {
      const pool = this.querySelector('#vv-header-image-pool');
      if (!pool) return;

      this.#removeClones();
      this.#showLegacyCards();
      this.#distributeDesktop(pool);
      this.#distributeMobile(pool);
    } catch (error) {
      // Graceful degradation: never let a render bug break the header.
      console.debug('[vv-header-image-distributor] distribute failed:', error);
    } finally {
      // Drop the records our own writes generated so they don't re-trigger us.
      this.#observer?.takeRecords();
      this.#distributing = false;
    }
  }

  /** Remove all clones we previously injected anywhere in the document. */
  #removeClones() {
    for (const clone of document.querySelectorAll(`[${CLONE_ATTR}]`)) {
      clone.remove();
    }
  }

  /**
   * Reverse every legacy-card hide we applied on a prior run, document-wide.
   * Runs at the top of distribute() (before re-injecting) so a panel that lost
   * its menu-image card restores its legacy cards, and so the hide never
   * compounds or leaves residue across morphs. JS-only: clears the marker and
   * the inline display we set; touches nothing the server owns.
   */
  #showLegacyCards() {
    for (const card of document.querySelectorAll(`[${LEGACY_HIDDEN_ATTR}]`)) {
      card.removeAttribute(LEGACY_HIDDEN_ATTR);
      if (card instanceof HTMLElement) {
        card.style.removeProperty('display');
      }
    }
  }

  /**
   * Hide the legacy (server-rendered, NON-clone) image-card <li> items in a list
   * a menu-image card was injected into, so the card REPLACES them. Reversible +
   * JS-only: marks each with [data-vv-menu-image-legacy-hidden] and inline
   * display:none, both cleared by #showLegacyCards() on the next run. Non-card
   * content is never in this list, so nav-link columns are unaffected.
   * @param {Element} list  the content list a menu-image clone was just appended to
   * @param {string} legacyItemSelector  selector matching legacy card <li> items
   * @returns {number}  how many legacy cards were hidden (0 when none present)
   */
  #hideLegacyCards(list, legacyItemSelector) {
    let hidden = 0;
    for (const item of list.querySelectorAll(`:scope > ${legacyItemSelector}`)) {
      if (item.hasAttribute(CLONE_ATTR)) continue; // never hide our own menu-image card
      item.setAttribute(LEGACY_HIDDEN_ATTR, '');
      if (item instanceof HTMLElement) {
        item.style.display = 'none';
      }
      hidden += 1;
    }
    return hidden;
  }

  /**
   * Group pooled cards by position into a Map<positionString, Element[]>,
   * preserving pool order.
   * @param {Element} pool
   * @param {'desktop' | 'mobile'} variant
   * @returns {Map<string, Element[]>}
   */
  #groupByPosition(pool, variant) {
    /** @type {Map<string, Element[]>} */
    const groups = new Map();
    for (const card of pool.querySelectorAll(`[data-vv-menu-image-card="${variant}"]`)) {
      const position = card.getAttribute('data-menu-image-position');
      if (!position) continue;
      const list = groups.get(position) ?? [];
      list.push(card);
      groups.set(position, list);
    }
    return groups;
  }

  // -- Desktop ---------------------------------------------------------------

  /**
   * Clone desktop cards into #submenu-{N} > .mega-menu__content >
   * ul.mega-menu__content-list, creating the content/list scaffold when the
   * panel is text-only, and re-apply the full server layout contract.
   * @param {Element} pool
   */
  #distributeDesktop(pool) {
    const groups = this.#groupByPosition(pool, 'desktop');

    for (const [position, cards] of groups) {
      const panel = document.getElementById(`submenu-${position}`);
      if (!panel) {
        // No matching panel (e.g. a childless top-level link). Documented no-op.
        console.debug(`[vv-header-image-distributor] no #submenu-${position}; skipping desktop menu-image`);
        continue;
      }

      const grid = panel.querySelector('.mega-menu__grid');
      if (!grid) {
        console.debug(`[vv-header-image-distributor] #submenu-${position} has no .mega-menu__grid; skipping`);
        continue;
      }

      const firstAspect = cards[0]?.getAttribute('data-menu-image-aspect') || '1';
      const { content, list } = this.#ensureDesktopScaffold(grid, firstAspect);

      // Inject card <li> clones (the card is the <li> rendered by mega-menu-card).
      let renderedCount = 0;
      let reservedUnits = 0;
      cards.forEach((card, index) => {
        const li = this.#extractCardLi(card);
        if (!li) return;

        const isWide = card.getAttribute('data-menu-image-wide') === 'true';
        if (isWide) {
          li.classList.add('mega-menu__content-list-item--wide');
        }
        // Server hides cards past max_metaobject_images_tablet (=1): first shown,
        // the rest hidden on tablet.
        if (index > 0) {
          li.classList.add('mega-menu__content-list-item--hidden-tablet');
        }

        li.setAttribute(CLONE_ATTR, '');
        list.appendChild(li);
        renderedCount += 1;
        reservedUnits += isWide ? 2 : 1;
      });

      if (renderedCount === 0) continue;

      // REPLACE: hide the legacy (non-clone) image cards now sharing this list,
      // so only the injected menu-image card(s) remain visible. JS-only + reversible.
      // Each hidden legacy card reserved 1 desktop column on the server grid
      // (mega-menu-list: metaobject_image_columns == legacy count); the menu-image
      // card takes their place, so the grid bump replaces that reservation rather
      // than stacking on top of it.
      const hiddenLegacy = this.#hideLegacyCards(list, 'li.mega-menu__content-list-item');

      this.#applyDesktopLayout({
        content,
        list,
        grid,
        position,
        renderedCount,
        reservedUnits,
        firstAspect,
        hiddenLegacy,
      });
    }
  }

  /**
   * Find or create .mega-menu__content > ul.mega-menu__content-list inside the
   * panel grid. Text-only panels lack the content span entirely.
   * @param {Element} grid
   * @param {string} aspect
   * @returns {{ content: Element, list: Element }}
   */
  #ensureDesktopScaffold(grid, aspect) {
    let content = grid.querySelector(':scope > .mega-menu__content--custom-images');
    if (!content) {
      // Reuse an existing non-custom .mega-menu__content if present, else create.
      content = document.createElement('span');
      content.className = 'mega-menu__content';
      content.style.setProperty('--menu-custom-image-aspect-ratio', aspect);
      grid.appendChild(content);
    }

    let list = content.querySelector(':scope > ul.mega-menu__content-list');
    if (!list) {
      list = document.createElement('ul');
      list.className = 'mega-menu__content-list list-unstyled';
      content.appendChild(list);
    }

    return { content, list };
  }

  /**
   * Re-apply every server layout var/class the morph would have produced for a
   * native menu-image panel (Contract 4), at both desktop and tablet breakpoints.
   * @param {{ content: Element, list: Element, grid: Element, position: string,
   *           renderedCount: number, reservedUnits: number, firstAspect: string,
   *           hiddenLegacy?: number }} args
   *   hiddenLegacy: count of legacy cards hidden in this panel (REPLACE). The
   *   server grid base already reserves one desktop column per legacy card, so
   *   the menu-image card's grid bump nets out that reservation to avoid an empty gap.
   */
  #applyDesktopLayout({
    content,
    list,
    grid,
    position,
    renderedCount,
    reservedUnits,
    firstAspect,
    hiddenLegacy = 0,
  }) {
    const multiple = renderedCount > 1;
    const imageClass = multiple ? 'mega-menu__content--multiple-images' : 'mega-menu__content--single-image';
    const listImageClass = multiple
      ? 'mega-menu__content-list--multiple-images'
      : 'mega-menu__content-list--single-image';
    const tabletContentColumns = Math.min(reservedUnits, 2);

    // .mega-menu__content span.
    content.classList.add('mega-menu__content', 'mega-menu__content--custom-images', imageClass);
    content.classList.remove(
      multiple ? 'mega-menu__content--single-image' : 'mega-menu__content--multiple-images'
    );
    content.style.setProperty('--menu-content-columns-desktop', String(reservedUnits));
    content.style.setProperty('--menu-content-columns-tablet', String(tabletContentColumns));
    content.style.setProperty('--menu-custom-image-aspect-ratio', firstAspect);

    // ul.mega-menu__content-list. Server caps tablet to 1 (mega-menu-list:624).
    list.classList.add('mega-menu__content-list', 'mega-menu__content-list--custom-images', listImageClass);
    list.classList.remove(
      multiple ? 'mega-menu__content-list--single-image' : 'mega-menu__content-list--multiple-images'
    );
    list.style.setProperty('--menu-content-columns-desktop', String(renderedCount));
    list.style.setProperty('--menu-content-columns-tablet', '1');

    // Grid track bump (mega-menu-list:469-472): add the reserved units to the
    // grid's column counts so the card content has room beside the nav columns.
    // Under REPLACE, subtract the legacy reservation the server baked into the
    // cached base (1 desktop col per legacy card; tablet capped at 2) so the
    // menu-image card replaces rather than stacks on the legacy columns.
    const legacyTabletReservation = Math.min(hiddenLegacy, 2);
    this.#bumpGridColumns(
      grid,
      reservedUnits - hiddenLegacy,
      tabletContentColumns - legacyTabletReservation
    );
  }

  /**
   * Add `reservedUnits` / `tabletUnits` to the grid's --menu-columns-* vars,
   * once per distribute() run (idempotent via a stored base).
   * @param {Element} grid
   * @param {number} reservedUnits
   * @param {number} tabletUnits
   */
  #bumpGridColumns(grid, reservedUnits, tabletUnits) {
    if (!(grid instanceof HTMLElement)) return;
    this.#bumpVar(grid, '--menu-columns-desktop', reservedUnits);
    this.#bumpVar(grid, '--menu-columns-tablet', tabletUnits);
  }

  /**
   * Idempotently bump a CSS column var: cache the server base in a data-* attr
   * keyed by the var name, then set value = base + delta on every run so repeated
   * distribute() calls (morph survival) never compound the bump.
   * @param {HTMLElement} el
   * @param {string} varName
   * @param {number} delta
   */
  #bumpVar(el, varName, delta) {
    const baseKey = `vvBase${varName.replace(/[^a-z0-9]/gi, '')}`;
    let base = el.dataset[baseKey];
    if (base === undefined) {
      const current = getComputedStyle(el).getPropertyValue(varName).trim();
      base = current === '' ? '0' : current;
      el.dataset[baseKey] = base;
    }
    const baseNum = parseFloat(base) || 0;
    el.style.setProperty(varName, String(baseNum + delta));
  }

  // -- Mobile ----------------------------------------------------------------

  /**
   * Clone mobile cards into the Nth top-level drawer item's
   * .menu-drawer__metaobject-content > ul.menu-drawer__metaobject-list, creating
   * the container when the item has no card content (GATE-D).
   * @param {Element} pool
   */
  #distributeMobile(pool) {
    const groups = this.#groupByPosition(pool, 'mobile');
    if (groups.size === 0) return;

    const menu = document.querySelector('.menu-drawer__menu.has-submenu');
    if (!menu) {
      console.debug('[vv-header-image-distributor] no .menu-drawer__menu.has-submenu; skipping mobile menu-image');
      return;
    }
    // Top-level items are the DIRECT children <li> of the has-submenu list
    // (class menu-drawer__list-item--deep/--flat, not plain menu-drawer__list-item).
    const items = [...menu.children].filter((node) => node.tagName === 'LI');

    for (const [position, cards] of groups) {
      const index = parseInt(position, 10) - 1;
      const item = items[index];
      if (!item) {
        console.debug(`[vv-header-image-distributor] no drawer item at position ${position}; skipping mobile menu-image`);
        continue;
      }

      const list = this.#ensureMobileContainer(item, cards[0]);
      if (!list) {
        // Childless link / no accordion host. Documented no-op limitation.
        console.debug(`[vv-header-image-distributor] no accordion host for drawer position ${position}; skipping`);
        continue;
      }

      let injected = 0;
      for (const card of cards) {
        const li = this.#extractCardLi(card);
        if (!li) continue;
        li.setAttribute(CLONE_ATTR, '');
        list.appendChild(li);
        injected += 1;
      }
      if (injected === 0) continue;

      // REPLACE: hide the legacy (non-clone) mobile image cards in this list so
      // only the injected menu-image card(s) show. JS-only + reversible. When the
      // list was freshly CREATED by #ensureMobileContainer it has no legacy items,
      // so this is a no-op there.
      this.#hideLegacyCards(list, 'li.menu-drawer__metaobject-list-item');
    }
  }

  /**
   * Find or CREATE .menu-drawer__metaobject-content > ul.menu-drawer__metaobject-list
   * inside the drawer item's accordion <details>. Returns the list, or null when
   * there is no host (childless link).
   * @param {Element} item   the top-level drawer <li>
   * @param {Element} firstCard  a mobile card wrapper (carries the aspect)
   * @returns {Element | null}
   */
  #ensureMobileContainer(item, firstCard) {
    // Existing container (GATE-D: Clothing/pos-3 has it).
    let content = item.querySelector('.menu-drawer__metaobject-content');
    if (content) {
      const existingList = content.querySelector(':scope > ul.menu-drawer__metaobject-list');
      if (existingList) return existingList;
    }

    // Host: the accordion <details> (live 2-level path) or, failing that, the
    // childlist <ul>'s parent. No host => childless link => null.
    const host =
      item.querySelector(':scope > accordion-custom > details') ??
      item.querySelector(':scope accordion-custom > details') ??
      item.querySelector('.menu-drawer__menu--childlist')?.parentElement;
    if (!host) return null;

    const aspect = firstCard?.getAttribute('data-menu-image-aspect') || MOBILE_DEFAULT_ASPECT;

    content = document.createElement('div');
    content.className =
      'menu-drawer__featured-content menu-drawer__featured-content--childlist menu-drawer__metaobject-content';
    content.style.setProperty('--menu-custom-image-aspect-ratio', aspect);

    const list = document.createElement('ul');
    list.className = 'menu-drawer__metaobject-list list-unstyled';
    content.appendChild(list);

    // Place after the childlist <ul> (legacy server position) when present, else
    // at the end of the host.
    const childlist = host.querySelector(':scope > ul.menu-drawer__menu--childlist');
    if (childlist && childlist.parentElement === host) {
      childlist.after(content);
    } else {
      host.appendChild(content);
    }

    return list;
  }

  // -- Shared ----------------------------------------------------------------

  /**
   * The card wrapper contains a single <li> (rendered by mega-menu-card). Clone
   * and return that <li> for injection; null when the wrapper has no <li> (a
   * skipped blank mobile image).
   * @param {Element} card  a [data-vv-menu-image-card] wrapper
   * @returns {Element | null}
   */
  #extractCardLi(card) {
    const li = card.querySelector(':scope > li');
    return li ? /** @type {Element} */ (li.cloneNode(true)) : null;
  }
}

if (!customElements.get('vv-header-image-distributor')) {
  customElements.define('vv-header-image-distributor', VVHeaderImageDistributor);
}
